import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { updateDefaultDates } from "../../../store/state/DefaultDatesSetter";
import { parseDateParams } from "../../../components/Search/Jvvnl/deepLink";
import MainPageContainer from "../../../components/MainPageContainer";
import Tooltip from "../../../components/tooltip";
import { SearchBox } from "../../../components/Search";
import { MetricCard, DataSummaryCard } from "./MetricCard";
import { PeakCard } from "./PeakCard";
import { RankTable, FeederTable, RankListModal } from "./RankTable";
import { NetConsumptionPopup } from "./NetConsumptionPopup";
import { FilterTab } from "../../../components/Filters";
import { formatDateForAPI } from "../../Rajasthan/Utility/const";
import {
  getJvvnlCircles,
  getJvvnlTranscoSubstationsList,
  getJvvnlDiscomSubstations,
  getJvvnlFeeders,
  getJvvnlGenerationMetrics,
  getJvvnlCircleDemandMetrics,
  getJvvnlTranscoDemandMetrics,
  getJvvnlDiscomSubDemandMetrics,
  getJvvnlFeederMetrics,
  getJvvnlFeederQuality,
  getJvvnlGroupQuality,
  getJvvnlCircleForecast,
  getJvvnlTranscoForecast,
  getJvvnlDiscomSubstationForecast,
  getJvvnlFeederForecast,
  getJvvnlFeederChart,
  getJvvnlGroupQualityForecast,
} from "../../../Rest_api/RestApi";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../models/chart_model";
import type { CriticalPeriodKind } from "../../../models/chart_model";
import type { JvvnlFeederChartRow } from "../../../models/jvvnl";
import type {
  JvvnlFeederMetricResult,
  JvvnlGenerationMetricResult,
  JvvnlFeederQualityRow,
  JvvnlGroupQualityRow,
  JvvnlGroupQualityGeneratorRow,
} from "../../../models/jvvnl";

type PillOption = { value: string | number; label: string };

const LOAD_GRANULARITY_OPTIONS = [
  "Circle",
  "Transco",
  "Discom Substation",
  "Feeder",
];

const GRANULARITY_LEVELS: Record<string, number> = {
  Circle: 1,
  Transco: 2,
  "Discom Substation": 3,
  Feeder: 4,
};

function roundMw(v: string | number | null | undefined): number {
  return parseFloat(String(v ?? 0)) || 0;
}
function roundMwh(v: string | number | null | undefined): number {
  return parseFloat(String(v ?? 0)) || 0;
}

type WeatherStats = {
  temp?: number | null;
  humid?: number | null;
  solar?: number | null;
};

function toWeatherStats(
  w:
    | {
        temp?: string | null;
        humidity?: string | null;
        solar_irradiance?: string | null;
      }
    | null
    | undefined,
): WeatherStats | null {
  if (!w) return null;
  return {
    temp: w.temp != null ? parseFloat(w.temp) : null,
    humid: w.humidity != null ? parseFloat(w.humidity) : null,
    solar: w.solar_irradiance != null ? parseFloat(w.solar_irradiance) : null,
  };
}

// ─── Skeletons ──────────────────────────────────────────────────────────────

function MetricCardSkeleton() {
  return (
    <div
      className="relative bg-white rounded-xl border border-[#E8EDF2] overflow-hidden"
      style={{ boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)" }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-[3.5px] bg-[#DFEAF2]" />
      <div className="pl-4 pr-3 pt-3 pb-3 flex flex-col gap-3">
        {/* icon + title */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#DFEAF2]" />
          <div className="h-3.5 w-20 bg-[#DFEAF2] rounded" />
        </div>
        {/* peak / avg cols */}
        <div className="flex gap-3">
          <div className="flex-1 space-y-1.5">
            <div className="flex gap-1.5">
              <div className="h-2 w-8 bg-[#DFEAF2] rounded" />
              <div className="h-2 w-20 bg-[#EEF4F8] rounded" />
            </div>
            <div className="h-7 w-24 bg-[#DFEAF2] rounded" />
          </div>
          <div className="w-px bg-[#DFEAF2] self-stretch" />
          <div className="flex-1 space-y-1.5">
            <div className="h-2 w-10 bg-[#DFEAF2] rounded" />
            <div className="h-7 w-24 bg-[#DFEAF2] rounded" />
          </div>
        </div>
        {/* weather section */}
        <div className="pt-2 border-t border-[#F0F4F8]">
          <div className="flex items-center gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-1.5 flex-1">
                <div className="w-6 h-6 rounded-md bg-[#DFEAF2] flex-shrink-0" />
                <div className="space-y-1">
                  <div className="h-2.5 w-10 bg-[#DFEAF2] rounded" />
                  <div className="h-2 w-8 bg-[#EEF4F8] rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PeakCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] pt-3 pb-2.5 px-4 flex flex-col gap-2">
      {/* title + date badge on left, value on right */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-20 bg-[#DFEAF2] rounded" />
          <div className="h-4 w-24 bg-[#EEF4F8] rounded-md" />
        </div>
        <div className="h-5 w-16 bg-[#DFEAF2] rounded" />
      </div>
      <div className="w-full h-px bg-[#DFEAF2]" />
      {/* weather stats */}
      <div className="flex justify-between mt-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="h-2 w-10 bg-[#DFEAF2] rounded" />
            <div className="h-4 w-14 bg-[#DFEAF2] rounded" />
            <div className="h-2 w-10 bg-[#EEF4F8] rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function RankTableSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3 flex flex-col gap-3">
      <div className="h-4 w-40 bg-[#DFEAF2] rounded" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="grid grid-cols-5 gap-2">
          <div className="h-3 w-full bg-[#DFEAF2] rounded col-span-2" />
          <div className="h-3 w-full bg-[#EEF4F8] rounded" />
          <div className="h-3 w-full bg-[#EEF4F8] rounded" />
          <div className="h-3 w-full bg-[#EEF4F8] rounded" />
        </div>
      ))}
    </div>
  );
}

// ─── Header (entity filters) ────────────────────────────────────────────────

function Header({
  loadGranularity,
  onGranularityChange,
  selectedLabel,
  onTitleClick,
}: {
  loadGranularity: string;
  onGranularityChange: (v: string) => void;
  selectedLabel?: string | null;
  onTitleClick?: () => void;
}) {
  return (
    <div className="bg-white flex justify-between px-4 items-center rounded-xl border border-[#DFEAF2] mb-4">
      <div className="flex items-center gap-2 py-2">
        {selectedLabel && onTitleClick ? (
          <button
            onClick={onTitleClick}
            title={`View detailed metrics for ${selectedLabel} (${loadGranularity})`}
            className="text-[15px] font-medium text-[#525062] inline-flex items-center gap-1.5 hover:text-[#14171F] transition-colors"
          >
            {selectedLabel}
            <span className="text-[#8C8A96] font-normal">
              ({loadGranularity})
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </svg>
          </button>
        ) : (
          <span className="text-[15px] font-medium text-[#525062]">
            {selectedLabel || loadGranularity}
            {selectedLabel && (
              <span className="text-[#8C8A96] font-normal ml-1">
                ({loadGranularity})
              </span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-end py-2">
        <FilterTab
          options={LOAD_GRANULARITY_OPTIONS}
          selected={[loadGranularity]}
          onClick={(opt) => onGranularityChange(opt)}
          variant="pill-outline"
        />
      </div>
    </div>
  );
}

const METRIC_TABS = ["Actual", "Forecast"] as const;
type MetricTab = (typeof METRIC_TABS)[number];
const FEEDER_VIEW_TABS = ["Table", "Chart"];

// ─── Page ───────────────────────────────────────────────────────────────────

const JvvnlMetric = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [loadGranularity, setLoadGranularity] = useState(
    () => searchParams.get("granularity") ?? "Circle",
  );
  const [prevGranularity, setPrevGranularity] = useState(loadGranularity);
  const [metricTab, setMetricTab] = useState<MetricTab>(() =>
    searchParams.get("tab") === "forecast" ? "Forecast" : "Actual",
  );
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );

  if (loadGranularity !== prevGranularity) {
    const options = ["Circle", "Transco", "Discom Substation", "Feeder"];
    const prevIdx = options.indexOf(prevGranularity);
    const currIdx = options.indexOf(loadGranularity);
    if (currIdx !== -1 && prevIdx !== -1 && currIdx !== prevIdx) {
      setSlideDirection(currIdx > prevIdx ? "right" : "left");
    }
    setPrevGranularity(loadGranularity);
  }

  // Critical-periods toggle lifted to page scope so it persists across the
  // feeder Chart↔Table switch and filter changes. In-memory only — resets to
  // OFF on full refresh / navigation away, per spec.
  const [criticalPeriodKinds, setCriticalPeriodKinds] = useState<
    Set<CriticalPeriodKind>
  >(() => new Set<CriticalPeriodKind>());
  const [loadRankView, setLoadRankView] = useState<"top" | "bottom">(() =>
    searchParams.get("rank") === "bottom" ? "bottom" : "top",
  );
  const [genRankView, setGenRankView] = useState<"top" | "bottom">(() =>
    searchParams.get("genrank") === "bottom" ? "bottom" : "top",
  );

  // Transient table highlight: when a search/ranking deep-link carries `hl`, the
  // matching table pulses briefly so the user sees which one their result landed
  // on. Cleared after the animation. Keys: load-top/load-bottom/gen (rank tables)
  // and fq-high/fq-low (Feeder data-quality tables).
  const [highlightTable, setHighlightTable] = useState<string | null>(() =>
    searchParams.get("hl"),
  );
  // Scroll + clear handled in a dedicated effect (below the loading-state
  // declarations) so the pulse waits for the real table to render, not skeletons.

  // Map a metric/peak card title → its highlight key, so a search result like
  // "Net Load (Transco · Forecast)" can pulse the exact card on arrival.
  const CARD_HL: Record<string, string> = {
    Load: "card-load",
    Generation: "card-generation",
    "Net Load": "card-netload",
    Energy: "card-energy",
    "Morning Peak": "card-morning",
    "Solar Peak": "card-solar",
    "Evening Peak": "card-evening",
  };
  // Class for a card wrapper given its highlight key.
  const hlClass = (key: string) =>
    `jvvnl-hl-wrap ${highlightTable === key ? "jvvnl-hl-on" : ""}`;
  const [feederView, setFeederView] = useState("Table");
  const [primaryWeather, setPrimaryWeather] = useState("Avg Temp");
  const [modalEntity, setModalEntity] = useState<{
    level: "transco" | "discom_sub" | "plant" | "circle" | "feeder";
    id: number | string;
    label: string;
  } | null>(null);
  // Full sortable list modal opened from a rank-table subheading.
  const [listModal, setListModal] = useState<"load" | "gen" | null>(null);
  const [listModalRows, setListModalRows] = useState<{
    top: JvvnlGroupQualityRow[];
    top_generators: JvvnlGroupQualityGeneratorRow[];
  }>({ top: [], top_generators: [] });
  const [listModalLoading, setListModalLoading] = useState(false);

  // Close NetConsumptionPopup modal on Esc key
  useEffect(() => {
    if (!modalEntity) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalEntity(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalEntity]);

  // Dates from redux (NewDynamicTab seeds it via JvvnlOverview.Metric)
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const range =
    DefaultDates?.Metric?.defaultRangeDate ??
    DefaultDates?.Overview?.defaultRangeDate;
  const dateParams = useMemo(() => {
    if (!range?.startDate || !range?.endDate) return null;
    return {
      startDate: formatDateForAPI(range.startDate),
      endDate: formatDateForAPI(range.endDate),
    };
  }, [range?.startDate, range?.endDate]);

  // Apply a search-deep-linked date into the shared date slice once per
  // search string. Metric reads defaultDates.Metric.defaultRangeDate (fallback
  // Overview); dispatch into the Metric tab so the page picks it up.
  const searchStr = searchParams.toString();
  const appliedDateRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (appliedDateRef.current === searchStr) return;
    const dm = parseDateParams(`?${searchStr}`);
    if (!dm) {
      appliedDateRef.current = searchStr;
      return;
    }
    const tab = DefaultDates?.Metric
      ? "Metric"
      : DefaultDates?.Overview
        ? "Overview"
        : null;
    if (!tab) return; // slice not seeded yet
    const startStr = dm.date ?? dm.startDate!;
    const endStr = dm.date ?? dm.endDate!;
    const startDate = new Date(`${startStr}T00:00:00`);
    const endDate = new Date(`${endStr}T00:00:00`);
    dispatch(
      updateDefaultDates({ activeTab: tab, dates: { startDate, endDate } }),
    );
    appliedDateRef.current = searchStr;
  }, [searchStr, DefaultDates, dispatch]);

  // Entity dropdowns
  const [allDiscomSubs, setAllDiscomSubs] = useState<any[]>([]);
  const [allFeeders, setAllFeeders] = useState<any[]>([]);
  const [allTranscos, setAllTranscos] = useState<PillOption[]>([]);
  // Transco ids belonging to the selected circle (A2 cascade). null = unfiltered.
  const [circleTranscoIds, setCircleTranscoIds] = useState<Set<number> | null>(
    null,
  );
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [circleList, setCircleList] = useState<PillOption[]>([]);

  const [circle, setCircle] = useState<PillOption | null>(() => {
    const v = searchParams.get("circle");
    const l = searchParams.get("circle_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });
  const [transco, setTransco] = useState<PillOption | null>(() => {
    const v = searchParams.get("transco");
    const l = searchParams.get("transco_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });
  const [discom, setDiscom] = useState<PillOption | null>(() => {
    const v = searchParams.get("discom");
    const l = searchParams.get("discom_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });
  const [feeder, setFeeder] = useState<PillOption | null>(() => {
    const v = searchParams.get("feeder");
    const l = searchParams.get("feeder_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });

  // Load circle options from API; default to first circle when none selected.
  useEffect(() => {
    let mounted = true;
    getJvvnlCircles()
      .then((circles) => {
        if (!mounted) return;
        const opts: PillOption[] = circles.map((c) => ({
          value: c.circle_id,
          label: c.circle_name,
        }));
        setCircleList(opts);
        const defaultCircle =
          opts.find((o) => o.label === "KOTPUTLI") ?? opts[0] ?? null;
        setCircle((prev) => prev ?? defaultCircle);
      })
      .catch(() => mounted && setCircleList([]));
    return () => {
      mounted = false;
    };
  }, []);

  // A2 cascade: fetch the transco ids that belong to the selected circle so the
  // transco dropdown (and downstream discom/feeder lists) scope to that circle.
  useEffect(() => {
    const circleId = circle ? Number(circle.value) : undefined;
    if (!circleId || isNaN(circleId)) {
      setCircleTranscoIds(null);
      return;
    }
    let mounted = true;
    getJvvnlTranscoSubstationsList(circleId)
      .then((res) => {
        if (!mounted) return;
        setCircleTranscoIds(
          new Set(
            res.results.map((r) => Number(r.id)).filter((n) => !isNaN(n)),
          ),
        );
      })
      .catch(() => mounted && setCircleTranscoIds(null));
    return () => {
      mounted = false;
    };
  }, [circle]);

  // Transco list scoped to the selected circle (A2). When the scope set is
  // unavailable, fall back to all transcos.
  const transcoList: PillOption[] = useMemo(() => {
    if (!circleTranscoIds) return allTranscos;
    return allTranscos.filter((t) => circleTranscoIds.has(Number(t.value)));
  }, [allTranscos, circleTranscoIds]);

  // Discom list filtered by selected transco
  const discomList: PillOption[] = useMemo(() => {
    const filtered = transco
      ? allDiscomSubs.filter((d) => d.transco_substation_id === transco.value)
      : circleTranscoIds
        ? allDiscomSubs.filter((d) =>
            circleTranscoIds.has(Number(d.transco_substation_id)),
          )
        : allDiscomSubs;
    return filtered.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [allDiscomSubs, transco, circleTranscoIds]);

  // Feeder list filtered by selected discom, or by selected transco's discoms
  const feederList: PillOption[] = useMemo(() => {
    let filtered = allFeeders;
    if (discom) {
      filtered = allFeeders.filter(
        (f) => f.discom_substation_id === discom.value,
      );
    } else if (transco) {
      const discomIds = new Set(
        allDiscomSubs
          .filter((d) => d.transco_substation_id === transco.value)
          .map((d) => d.discom_substation_id),
      );
      filtered = allFeeders.filter((f) =>
        discomIds.has(f.discom_substation_id),
      );
    } else if (circleTranscoIds) {
      const discomIds = new Set(
        allDiscomSubs
          .filter((d) => circleTranscoIds.has(Number(d.transco_substation_id)))
          .map((d) => d.discom_substation_id),
      );
      filtered = allFeeders.filter((f) =>
        discomIds.has(f.discom_substation_id),
      );
    }
    return filtered.map((f) => ({
      value: f.feeder_id,
      label: f.feeder_name || String(f.feeder_id),
    }));
  }, [allFeeders, allDiscomSubs, discom, transco, circleTranscoIds]);

  useEffect(() => {
    // Discom/feeder entity lists only needed at Transco, Discom Substation, or Feeder granularity
    if (loadGranularity === "Circle") return;
    // Skip if we already have the data
    if (allDiscomSubs.length > 0 && allFeeders.length > 0) return;

    let mounted = true;
    setLoadingEntities(true);
    const needFeeders =
      loadGranularity === "Feeder" ||
      loadGranularity === "Discom Substation" ||
      loadGranularity === "Transco";
    Promise.all([
      allDiscomSubs.length === 0
        ? getJvvnlDiscomSubstations().catch(() => [])
        : Promise.resolve(allDiscomSubs),
      needFeeders && allFeeders.length === 0
        ? getJvvnlFeeders().catch(() => [])
        : Promise.resolve(allFeeders),
    ])
      .then(([discoms, feeders]) => {
        if (!mounted) return;
        setAllDiscomSubs(discoms);
        setAllFeeders(feeders);
        // Build transco list from discom data — unique transcos filtered to allowed set
        const seen = new Set<number>();
        const transcos: PillOption[] = [];
        for (const d of discoms as any[]) {
          if (d.transco_substation_id && !seen.has(d.transco_substation_id)) {
            seen.add(d.transco_substation_id);
            transcos.push({
              value: d.transco_substation_id,
              label: d.transco_substation_name,
            });
          }
        }
        setAllTranscos(transcos);
      })
      .finally(() => mounted && setLoadingEntities(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGranularity]);

  // Cascade selection down to children of current granularity.
  // If a parent is selected (e.g. Transco at Discom Substation granularity),
  // expand to all children under that parent. Omit when nothing selected.
  const selectedEntityIds = useMemo<Array<number> | undefined>(() => {
    if (loadGranularity === "Circle") {
      if (circle) return [Number(circle.value)];
      return undefined;
    }
    if (loadGranularity === "Transco") {
      if (transco) return [Number(transco.value)];
      return undefined;
    }
    if (loadGranularity === "Discom Substation") {
      if (discom) return [Number(discom.value)];
      if (transco) {
        const ids = allDiscomSubs
          .filter((d) => d.transco_substation_id === transco.value)
          .map((d) => Number(d.discom_substation_id))
          .filter((n) => !isNaN(n));
        return ids.length ? ids : undefined;
      }
      return undefined;
    }
    if (loadGranularity === "Feeder") {
      if (feeder) return [Number(feeder.value)];
      if (discom) {
        const ids = allFeeders
          .filter((f) => f.discom_substation_id === discom.value)
          .map((f) => Number(f.feeder_id))
          .filter((n) => !isNaN(n));
        return ids.length ? ids : undefined;
      }
      if (transco) {
        const discomIds = new Set(
          allDiscomSubs
            .filter((d) => d.transco_substation_id === transco.value)
            .map((d) => d.discom_substation_id),
        );
        const ids = allFeeders
          .filter((f) => discomIds.has(f.discom_substation_id))
          .map((f) => Number(f.feeder_id))
          .filter((n) => !isNaN(n));
        return ids.length ? ids : undefined;
      }
      return undefined;
    }
    return undefined;
  }, [
    loadGranularity,
    circle,
    transco,
    discom,
    feeder,
    allDiscomSubs,
    allFeeders,
  ]);

  // All feeder ids under the selected circle (circle → transcos → discoms →
  // feeders), used to scope the Feeder-tab quality tables + chart to the circle
  // when no narrower transco/discom/feeder is selected. undefined until the
  // discom/feeder lists + circle scope resolve.
  const circleFeederIds = useMemo<Array<number> | undefined>(() => {
    if (!circleTranscoIds) return undefined;
    const discomIds = new Set(
      allDiscomSubs
        .filter((d) => circleTranscoIds.has(Number(d.transco_substation_id)))
        .map((d) => d.discom_substation_id),
    );
    const ids = allFeeders
      .filter((f) => discomIds.has(f.discom_substation_id))
      .map((f) => Number(f.feeder_id))
      .filter((n) => !isNaN(n));
    return ids.length ? ids : undefined;
  }, [circleTranscoIds, allDiscomSubs, allFeeders]);

  // Group-quality scope = the DEEPEST currently-selected entity (level + its id),
  // independent of the granularity tab. With only a circle selected we send
  // level=circle&ids=<circleId>; selecting a child swaps to that child's level
  // and id. The server then knows what ids=… actually refers to.
  const rankScope = useMemo<{
    level: "circle" | "transco" | "discom_sub" | "feeder";
    ids: Array<number> | undefined;
  }>(() => {
    // Guard against non-numeric .value (undefined/""/label strings) which would
    // otherwise coerce to NaN and get serialized as ids=NaN in the query string.
    const asId = (v: unknown): Array<number> | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? [n] : undefined;
    };
    if (feeder) return { level: "feeder", ids: asId(feeder.value) };
    if (discom) return { level: "discom_sub", ids: asId(discom.value) };
    if (transco) return { level: "transco", ids: asId(transco.value) };
    if (circle) return { level: "circle", ids: asId(circle.value) };
    return { level: "circle", ids: undefined };
  }, [circle, transco, discom, feeder]);

  // Group-quality scope for the rank-table widget. Unlike rankScope (which
  // sources the selected entity's OWN metrics), the widget ranks CHILDREN one
  // level below the granularity tab. The API's `level` param names the PARENT
  // of the ranked rows (== the granularity tab's own level), and ancestor
  // scoping (`ids` / `circle_id` / `transco_id`) restricts those children to
  // the currently-selected entity. See the group-quality scoping spec:
  //   Circle tab, circle selected   → level=circle,     ids=<circleId>
  //   Transco tab, circle selected  → level=transco,    circle_id=<circleId>
  //   Transco tab, transco selected → level=transco,    ids=<transcoId>
  //   Discom tab, circle selected   → level=discom_sub, circle_id=<circleId>
  //   Discom tab, transco selected  → level=discom_sub, transco_id=<transcoId>
  //   Discom tab, discom selected   → level=discom_sub, ids=<discomSubId>
  const groupQualityScope = useMemo<{
    level: "circle" | "transco" | "discom_sub";
    ids?: Array<number>;
    circle_id?: Array<number>;
    transco_id?: Array<number>;
  }>(() => {
    const asId = (v: unknown): Array<number> | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? [n] : undefined;
    };
    // Ranked-rows' parent level == the granularity tab's own level.
    const level =
      loadGranularity === "Circle"
        ? "circle"
        : loadGranularity === "Transco"
          ? "transco"
          : "discom_sub"; // "Discom Substation"

    const circleId = circle ? asId(circle.value) : undefined;
    const transcoId = transco ? asId(transco.value) : undefined;
    const discomId = discom ? asId(discom.value) : undefined;

    // Deepest selected entity AT OR ABOVE `level` decides the scope param.
    // A selection strictly deeper than `level` (e.g. discom picked while the
    // tab is Transco) can't scope this widget and is ignored.
    if (level === "discom_sub") {
      if (discom) return { level, ids: discomId };
      if (transco) return { level, transco_id: transcoId };
      return { level, circle_id: circleId };
    }
    if (level === "transco") {
      if (transco) return { level, ids: transcoId };
      return { level, circle_id: circleId };
    }
    // level === "circle"
    return { level, ids: circleId };
  }, [loadGranularity, circle, transco, discom]);

  // ── Data state ──
  const [groupDemand, setGroupDemand] =
    useState<JvvnlFeederMetricResult | null>(null);
  // Data-quality aggregate for the Feeder-tab Data Summary card. The circle/
  // transco demand endpoints don't carry available/zeros/nulls %, so we fetch
  // the feeder endpoint combined across the circle's feeders to get a true mean.
  const [dataSummary, setDataSummary] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [forecastDemand, setForecastDemand] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [forecastDate, setForecastDate] = useState<string | null>(null);
  const [genResults, setGenResults] = useState<JvvnlGenerationMetricResult[]>(
    [],
  );
  const [feederQualityHigh, setFeederQualityHigh] = useState<
    JvvnlFeederMetricResult[]
  >([]);
  const [feederQualityLow, setFeederQualityLow] = useState<
    JvvnlFeederMetricResult[]
  >([]);

  const [groupQuality, setGroupQuality] = useState<{
    top: JvvnlGroupQualityRow[];
    bottom: JvvnlGroupQualityRow[];
    top_generators: JvvnlGroupQualityGeneratorRow[];
    bottom_generators: JvvnlGroupQualityGeneratorRow[];
  }>({ top: [], bottom: [], top_generators: [], bottom_generators: [] });
  const [forecastGroupQuality, setForecastGroupQuality] = useState<{
    top: JvvnlGroupQualityRow[];
    bottom: JvvnlGroupQualityRow[];
    top_generators: JvvnlGroupQualityGeneratorRow[];
    bottom_generators: JvvnlGroupQualityGeneratorRow[];
  }>({ top: [], bottom: [], top_generators: [], bottom_generators: [] });
  const [loadingForecastRank, setLoadingForecastRank] = useState(false);
  const [feederQuality, setFeederQuality] = useState<{
    high: JvvnlFeederQualityRow[];
    low: JvvnlFeederQualityRow[];
  }>({ high: [], low: [] });

  const [feederChartRows, setFeederChartRows] = useState<JvvnlFeederChartRow[]>(
    [],
  );
  const [loadingFeederChart, setLoadingFeederChart] = useState(false);

  const [loadingCombined, setLoadingCombined] = useState(false);
  const [loadingRank, setLoadingRank] = useState(false);
  const [loadingFeederQuality, setLoadingFeederQuality] = useState(false);

  // Render-phase staleness guard for the rank tables. groupQuality /
  // forecastGroupQuality hold rows for a *previous* scope/tab until the next
  // fetch resolves. The fetch effects run after paint, so on a tab/filter
  // change there is one paint where the loading flag is still false but the
  // data belongs to the old scope (or is empty) — that's the "no data then
  // data" flash. We stamp the scope+tab key when each fetch resolves; the JSX
  // shows skeletons whenever the current key doesn't match the stamped one.
  const rankKey = `${groupQualityScope.level}|${(groupQualityScope.ids ?? []).join(",")}|c${(groupQualityScope.circle_id ?? []).join(",")}|t${(groupQualityScope.transco_id ?? []).join(",")}|${dateParams?.startDate ?? ""}|${dateParams?.endDate ?? ""}`;
  const rankFetchedKeyRef = React.useRef<string | null>(null);
  const forecastRankFetchedKeyRef = React.useRef<string | null>(null);
  const rankReady = rankFetchedKeyRef.current === rankKey;
  const forecastRankReady = forecastRankFetchedKeyRef.current === rankKey;

  // Drive the table highlight: wait until the relevant table's data has loaded
  // (so we pulse/scroll the real table, not its skeleton), scroll it into view,
  // then clear the flag once the animation has played so it can re-fire later.
  const highlightDoneRef = React.useRef<string | null>(null);
  const lastHlRef = React.useRef<string | null>(searchParams.get("hl"));
  const highlightTimersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    if (!highlightTable) {
      highlightDoneRef.current = null;
      return;
    }
    if (highlightDoneRef.current === highlightTable) return; // already animated
    const tablesLoading =
      loadingRank ||
      loadingForecastRank ||
      loadingFeederQuality ||
      loadingCombined;
    if (tablesLoading) return; // wait for the real table/card to render
    highlightDoneRef.current = highlightTable;
    // Schedule scroll + clear ONCE. Don't return a cleanup that cancels these on
    // unrelated dep changes (loading flags toggle during tab/page navigation),
    // or the clear timer gets repeatedly cancelled and the pulse never clears.
    highlightTimersRef.current.push(
      setTimeout(() => {
        document
          .querySelector(".jvvnl-hl-on")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120),
    );
    highlightTimersRef.current.push(
      setTimeout(() => setHighlightTable(null), 3000),
    );
  }, [
    highlightTable,
    loadingRank,
    loadingForecastRank,
    loadingFeederQuality,
    loadingCombined,
  ]);
  // Cancel any pending highlight timers on unmount only.
  useEffect(
    () => () => {
      highlightTimersRef.current.forEach(clearTimeout);
      highlightTimersRef.current = [];
    },
    [],
  );

  // ── Fetch: combined (top metric + peak cards) + generation total ──
  // Sourced from the deepest selected entity (rankScope): only-circle → circle
  // endpoint; pick a transco/discom/feeder → that entity's endpoint + id.
  useEffect(() => {
    if (!dateParams) return;
    if (!rankScope.ids) return; // wait for selection/circle list to resolve
    let mounted = true;
    setLoadingCombined(true);

    const dateArgs = {
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
    };
    const groupFetcher =
      rankScope.level === "feeder"
        ? getJvvnlFeederMetrics({
            ...dateArgs,
            level: "feeder",
            ids: rankScope.ids,
          })
        : rankScope.level === "transco"
          ? getJvvnlTranscoDemandMetrics({
              ...dateArgs,
              level: "transco",
              ids: rankScope.ids,
            })
          : rankScope.level === "discom_sub"
            ? getJvvnlDiscomSubDemandMetrics({
                ...dateArgs,
                level: "discom_sub",
                ids: rankScope.ids,
              })
            : getJvvnlCircleDemandMetrics({
                ...dateArgs,
                level: "circle",
                ids: rankScope.ids,
              });

    Promise.all([
      getJvvnlGenerationMetrics({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        level: "plant",
      }).catch(() => null),
      groupFetcher.catch(() => null),
    ])
      .then(([gen, grp]) => {
        if (!mounted) return;
        setGenResults(gen?.results ?? []);
        setGroupDemand(grp?.results?.[0] ?? null);
      })
      .finally(() => mounted && setLoadingCombined(false));

    return () => {
      mounted = false;
    };
  }, [dateParams, rankScope]);

  // ── Fetch: forecast demand ──
  // Forecast cards mirror the actual cards: sourced from the deepest selected
  // entity (rankScope) at the matching forecast endpoint.
  useEffect(() => {
    if (!rankScope.ids) return;
    let mounted = true;
    const dateArgs = dateParams
      ? { startDate: dateParams.startDate, endDate: dateParams.endDate }
      : {};
    const ids = rankScope.ids;
    const fetcher =
      rankScope.level === "feeder"
        ? getJvvnlFeederForecast({ ids, ...dateArgs })
        : rankScope.level === "transco"
          ? getJvvnlTranscoForecast({ ids, ...dateArgs })
          : rankScope.level === "discom_sub"
            ? getJvvnlDiscomSubstationForecast({
                level: "discom_sub",
                ids,
                ...dateArgs,
              })
            : getJvvnlCircleForecast({ ids, ...dateArgs });
    fetcher
      .catch(() => null)
      .then((demandRes) => {
        if (!mounted) return;
        setForecastDemand(demandRes?.results?.[0] ?? null);
        setForecastDate(demandRes?.forecast_date ?? null);
      });
    return () => {
      mounted = false;
    };
  }, [rankScope, dateParams]);

  // ── Fetch: rank tables (top/bottom 3) ──
  // Both Demand and Generation tabs source from /group-quality/ — demand uses
  // top/bottom, generation uses top_generators/bottom_generators.
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity === "Feeder") return;
    if (
      loadGranularity !== "Circle" &&
      loadGranularity !== "Transco" &&
      loadGranularity !== "Discom Substation"
    )
      return;
    let mounted = true;
    setLoadingRank(true);
    // Rank the children one level below the granularity tab, scoped to the
    // selected ancestor (see groupQualityScope).
    getJvvnlGroupQuality({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      level: groupQualityScope.level,
      limit: 3,
      ids: groupQualityScope.ids,
      circle_id: groupQualityScope.circle_id,
      transco_id: groupQualityScope.transco_id,
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setGroupQuality({
          top: res?.top ?? [],
          bottom: res?.bottom ?? [],
          top_generators: res?.top_generators ?? [],
          bottom_generators: res?.bottom_generators ?? [],
        });
        rankFetchedKeyRef.current = rankKey;
      })
      .finally(() => mounted && setLoadingRank(false));
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity, groupQualityScope]);

  // ── Fetch: forecast rank tables (top/bottom via /group-quality-forecast/) ──
  useEffect(() => {
    if (metricTab !== "Forecast") return;
    if (loadGranularity === "Feeder") {
      setForecastGroupQuality({
        top: [],
        bottom: [],
        top_generators: [],
        bottom_generators: [],
      });
      return;
    }
    let mounted = true;
    setLoadingForecastRank(true);
    getJvvnlGroupQualityForecast({
      level: groupQualityScope.level,
      limit: 3,
      ids: groupQualityScope.ids,
      circle_id: groupQualityScope.circle_id,
      transco_id: groupQualityScope.transco_id,
      ...(dateParams
        ? { startDate: dateParams.startDate, endDate: dateParams.endDate }
        : {}),
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setForecastGroupQuality({
          top: res?.top ?? [],
          bottom: res?.bottom ?? [],
          top_generators: res?.top_generators ?? [],
          bottom_generators: res?.bottom_generators ?? [],
        });
        forecastRankFetchedKeyRef.current = rankKey;
      })
      .finally(() => mounted && setLoadingForecastRank(false));
    return () => {
      mounted = false;
    };
  }, [metricTab, loadGranularity, groupQualityScope, dateParams]);

  // ── Fetch: full sortable list (rank-table subheading modal) ──
  // limit + gen_limit omitted → server returns the complete list in
  // top / top_generators (bottom* empty). Respects level, ids, dates and tab.
  useEffect(() => {
    if (!listModal) return;
    if (
      loadGranularity !== "Circle" &&
      loadGranularity !== "Transco" &&
      loadGranularity !== "Discom Substation"
    )
      return;
    let mounted = true;
    setListModalLoading(true);
    setListModalRows({ top: [], top_generators: [] });
    // Same scope as the rank tables (children below the granularity tab,
    // restricted to the selected ancestor).
    // Request a large fixed cap for both children and generators so the modal
    // always gets the full list (some endpoints default to top-3 when omitted).
    const FULL = 50;
    const fetcher =
      metricTab === "Forecast"
        ? getJvvnlGroupQualityForecast({
            level: groupQualityScope.level,
            limit: FULL,
            gen_limit: FULL,
            ids: groupQualityScope.ids,
            circle_id: groupQualityScope.circle_id,
            transco_id: groupQualityScope.transco_id,
            ...(dateParams
              ? {
                  startDate: dateParams.startDate,
                  endDate: dateParams.endDate,
                }
              : {}),
          })
        : dateParams
          ? getJvvnlGroupQuality({
              startDate: dateParams.startDate,
              endDate: dateParams.endDate,
              level: groupQualityScope.level,
              limit: FULL,
              gen_limit: FULL,
              ids: groupQualityScope.ids,
              circle_id: groupQualityScope.circle_id,
              transco_id: groupQualityScope.transco_id,
            })
          : Promise.resolve(null);
    fetcher
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setListModalRows({
          top: res?.top ?? [],
          top_generators: res?.top_generators ?? [],
        });
      })
      .finally(() => mounted && setListModalLoading(false));
    return () => {
      mounted = false;
    };
  }, [listModal, loadGranularity, groupQualityScope, dateParams, metricTab]);

  // ── Fetch: Data Summary aggregate (Feeder-tab card) ──
  // Combined feeder-metrics over the selected scope's feeders → true mean
  // available/zeros/nulls %. Only the feeder endpoint carries these fields.
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity !== "Feeder") {
      setDataSummary(null);
      return;
    }
    let mounted = true;
    getJvvnlFeederMetrics({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      level: "feeder",
      ids: selectedEntityIds ?? circleFeederIds,
      // Auto-combines server-side when ids omitted or has 2+ entries.
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setDataSummary(res?.results?.[0] ?? null);
      });
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity, selectedEntityIds, circleFeederIds]);

  // ── Fetch: feeder data-quality tables (via /feeder-quality/) ──
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity !== "Feeder") return;
    let mounted = true;
    setLoadingFeederQuality(true);
    getJvvnlFeederQuality({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      high_threshold: 80,
      low_threshold: 20,
      limit: 3,
      // Always scoped to the selected circle; child selections don't affect it.
      circleId: circle?.value,
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setFeederQuality({
          high: res?.above_threshold ?? [],
          low: res?.below_threshold ?? [],
        });
        setFeederQualityHigh([]);
        setFeederQualityLow([]);
      })
      .finally(() => mounted && setLoadingFeederQuality(false));
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity, circle?.value]);

  // ── Fetch: feeder chart (top-N feeders by energy) ──
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity !== "Feeder") {
      setFeederChartRows([]);
      return;
    }
    let mounted = true;
    setLoadingFeederChart(true);
    getJvvnlFeederChart({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      // Always scoped to the selected circle; child selections don't affect it.
      circleId: circle?.value,
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setFeederChartRows(res?.results ?? []);
      })
      .finally(() => mounted && setLoadingFeederChart(false));
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity, circle?.value]);

  // ── Derive feeder chart echarts data ──
  const feederChartData = useMemo(() => {
    if (!feederChartRows.length) return { data: [], legends: [] };
    const data = feederChartRows.map((r) => ({
      name: r.name,
      "Total Energy": r.total_kwh,
    }));
    return {
      data,
      legends: [
        {
          dataKey: "Total Energy",
          name: "Total Energy",
          unit: "kWh",
          type: ChartType.Bar,
        },
      ],
    };
  }, [feederChartRows]);

  // Units follow the data SOURCE (deepest selected entity), not the tab. Feeder
  // & Discom-substation endpoints emit kW/kWh; transco & circle emit MW/MWh.
  const { loadUnit, energyUnit } = useMemo(() => {
    if (rankScope.level === "feeder" || rankScope.level === "discom_sub") {
      return { loadUnit: "kW", energyUnit: "kWh" };
    }
    return { loadUnit: "MW", energyUnit: "MWh" };
  }, [rankScope.level]);

  // Scale kW/kWh values up to MW/MWh on the inline cards when they exceed 3
  // digits (feeder/discom source only). Modals are unaffected.
  const autoScaleUnit =
    rankScope.level === "feeder" || rankScope.level === "discom_sub";

  // ── Derive metric cards (Demand, Total Energy, Total Gen, Net Load) ──
  // All card data comes from the circle endpoint (groupDemand) at every
  // granularity, so there is no longer a Feeder special-case.
  const metricCards = useMemo(() => {
    const demandFull = groupDemand?.metrics.full;
    const nullableRound = (
      v: string | number | null | undefined,
    ): number | null => {
      if (v == null) return null;
      const n = parseFloat(String(v));
      return isNaN(n) ? null : n;
    };
    const demandPeak = nullableRound(demandFull?.peak_mw);
    const demandAvg = nullableRound(demandFull?.avg_mw);
    const totalEnergy = nullableRound(demandFull?.total_mwh);
    // Generation always sourced from group-demand endpoint (circle/transco/discom-sub).
    // When groupDemand is null (no group data for this date), show null — consistent
    // with Load/Energy/NetLoad which also go null when groupDemand is absent.
    const gPeakGen = groupDemand?.metrics.full?.peak_generation_mw;
    const gAvgGen = groupDemand?.metrics.full?.avg_generation_mw;
    const totalGenMW: number | null =
      groupDemand != null ? nullableRound(gPeakGen) : null;
    const totalGenAvgMW: number | null =
      groupDemand != null ? nullableRound(gAvgGen) : null;
    // Prefer server-computed net (per-bucket from group-demand endpoint) when available
    const gPeakNet = groupDemand?.metrics.full?.peak_net_mw;
    const gAvgNet = groupDemand?.metrics.full?.avg_net_mw;
    const netPeak: number | null =
      groupDemand != null ? nullableRound(gPeakNet) : null;
    const netAvg: number | null =
      groupDemand != null ? nullableRound(gAvgNet) : null;

    const genFull = genResults[0]?.metrics.full;
    const groupGenDate = groupDemand?.metrics.full?.peak_generation_date;
    const groupGenSlot = groupDemand?.metrics.full?.peak_generation_slot_48;
    const groupGenPeakWeather =
      groupDemand?.metrics.full?.peak_generation_weather;
    const base = [
      {
        title: "Load",
        peakValue: demandPeak,
        averageValue: demandAvg,
        unit: loadUnit,
        accentColor: "#E25A1F",
        averageColor: "#E25A1F",
        peakDate: demandFull?.peak_date,
        peakSlot: demandFull?.peak_slot_48 ?? null,
        peakWeather: toWeatherStats(demandFull?.peak_weather),
        // Load tab shows peak_weather in the main weather row (not avg_weather).
        avgWeather: toWeatherStats(demandFull?.peak_weather),
        theme: {
          barBackground: "linear-gradient(180deg, #ED9D2B 0%, #D8800F 100%)",
          iconBackground: "linear-gradient(150deg, #ED9D2B 0%, #D8800F 100%)",
          iconColor: "white",
          titleColor: "#A8540B",
          peakLabelColor: "#A8540B80",
          peakTextColor: "#A8540B",
          peakDateBg: "#A8540B15",
          peakDateColor: "#A8540B",
          peakSlotBg: "#A8540B",
        },
      },
      {
        title: "Generation",
        peakValue: totalGenMW,
        averageValue: totalGenAvgMW,
        unit: loadUnit,
        accentColor: "#606679",
        averageColor: "#606679",
        peakDate:
          groupGenDate ?? genFull?.peak_generation_date ?? genFull?.peak_date,
        peakSlot:
          groupGenSlot ??
          genFull?.peak_generation_slot_48 ??
          genFull?.peak_slot_48 ??
          null,
        peakWeather:
          toWeatherStats(groupGenPeakWeather) ??
          toWeatherStats(genFull?.peak_weather),
        // Generation tab shows peak_generation_weather in the main row.
        avgWeather:
          toWeatherStats(groupGenPeakWeather) ??
          toWeatherStats(genFull?.peak_weather),
      },
      {
        title: "Net Load",
        peakValue: netPeak,
        averageValue: netAvg,
        unit: loadUnit,
        accentColor: "#3A7E54",
        averageColor: "#3A7E54",
        peakDate:
          netPeak != null
            ? (demandFull?.peak_net_date ?? demandFull?.peak_date)
            : undefined,
        peakSlot:
          netPeak != null
            ? (demandFull?.peak_net_slot_48 ?? demandFull?.peak_slot_48 ?? null)
            : null,
        peakWeather: toWeatherStats(
          demandFull?.peak_net_weather ?? demandFull?.peak_weather,
        ),
        // Net Load tab shows peak_net_weather in the main row.
        avgWeather: toWeatherStats(
          demandFull?.peak_net_weather ?? demandFull?.peak_weather,
        ),
      },
      {
        title: "Energy",
        peakValue: totalEnergy,
        averageValue: nullableRound(demandFull?.avg_mwh),
        unit: energyUnit,
        peakLabel: "Total",
        accentColor: "#2563EB",
        averageColor: "#2563EB",
        peakWeather: toWeatherStats(demandFull?.peak_weather),
        avgWeather: toWeatherStats(demandFull?.avg_weather),
      },
    ];
    return base;
  }, [genResults, groupDemand, loadUnit, energyUnit]);

  // ── Derive peak cards (Morning/Solar/Evening) ──
  const peakCards = useMemo(() => {
    // Always the circle endpoint, every granularity.
    const m = groupDemand?.metrics;
    const toVal = (mw: string | number | undefined | null) =>
      mw == null ? null : roundMw(mw);
    const unit = loadUnit;
    return [
      {
        title: "Morning Peak",
        peakBlock: m?.morning?.peak_slot_48 ?? null,
        peakDate: m?.morning?.peak_date ?? null,
        peakValue: toVal(m?.morning?.peak_mw),
        unit,
        // FRONTEND_API 7: per-bucket avg_weather removed; show peak_weather.
        weather: toWeatherStats(
          m?.morning?.avg_weather ?? m?.morning?.peak_weather,
        ),
        weatherPrevDay: null,
      },
      {
        title: "Solar Peak",
        peakBlock: m?.solar?.peak_slot_48 ?? null,
        peakDate: m?.solar?.peak_date ?? null,
        peakValue: toVal(m?.solar?.peak_mw),
        unit,
        weather: toWeatherStats(
          m?.solar?.avg_weather ?? m?.solar?.peak_weather,
        ),
        weatherPrevDay: null,
      },
      {
        title: "Evening Peak",
        peakBlock: m?.evening?.peak_slot_48 ?? null,
        peakDate: m?.evening?.peak_date ?? null,
        peakValue: toVal(m?.evening?.peak_mw),
        unit,
        weather: toWeatherStats(
          m?.evening?.avg_weather ?? m?.evening?.peak_weather,
        ),
        weatherPrevDay: null,
      },
    ];
  }, [groupDemand, loadUnit]);

  // ── Derive rank tables (load + generation, each with top/bottom) ──
  const rankRows = useMemo(() => {
    const toDemandRow = (r: JvvnlGroupQualityRow) => ({
      entity: r.name || `#${r.entity_id}`,
      entityId: r.entity_id,
      avgDemand: roundMw(r.avg_demand_mw ?? 0),
      peakDemand: roundMw(r.peak_demand_mw ?? 0),
      avgTemp: r.avg_temp ?? 0,
      avgHumidity: r.avg_humidity ?? null,
      avgSolarIrradiance: r.avg_solar_irradiance ?? null,
      netLoad: roundMw(r.avg_net_mw ?? 0),
      peakDate: r.peak_demand_date ?? null,
      peakSlot: r.peak_demand_slot_48 ?? null,
    });

    const toGenRow = (r: JvvnlGroupQualityGeneratorRow) => ({
      entity: r.name || `#${r.entity_id}`,
      entityId: r.entity_id,
      avgDemand: parseFloat(String(r.avg_generation_mw ?? 0)) || 0,
      peakDemand: parseFloat(String(r.peak_generation_mw ?? 0)) || 0,
      avgTemp: r.avg_temp ?? null,
      avgHumidity: r.avg_humidity ?? null,
      avgSolarIrradiance: r.avg_solar_irradiance ?? null,
      netLoad: roundMwh(r.total_generation_mwh ?? 0),
      peakDate: r.peak_generation_date ?? null,
      peakSlot: r.peak_generation_slot_48 ?? null,
      pctLoadCovered:
        r.pct_load_covered != null
          ? isNaN(parseFloat(String(r.pct_load_covered)))
            ? null
            : parseFloat(String(r.pct_load_covered))
          : null,
    });

    const src = metricTab === "Forecast" ? forecastGroupQuality : groupQuality;
    return {
      loadTop: src.top.map(toDemandRow),
      loadBottom: src.bottom.map(toDemandRow),
      genTop: src.top_generators.map(toGenRow),
      genBottom: src.bottom_generators.map(toGenRow),
      toDemandRow,
      toGenRow,
    };
  }, [groupQuality, forecastGroupQuality, metricTab]);

  // Full-list modal rows (mapped with the same shape as rank-table rows).
  const listModalData = useMemo(() => {
    return {
      load: listModalRows.top.map(rankRows.toDemandRow),
      gen: listModalRows.top_generators.map(rankRows.toGenRow),
    };
  }, [listModalRows, rankRows]);

  const feederQualityRows = useMemo(() => {
    // Prefer server /feeder-quality/ rows when present
    if (feederQuality.high.length || feederQuality.low.length) {
      const toRow = (r: JvvnlFeederQualityRow) => ({
        entity: r.name || `#${r.entity_id}`,
        entityId: r.entity_id,
        availablePct: Number(r.available_pct.toFixed(2)),
        zerosPct: Number(r.zeros_pct.toFixed(2)),
        nansPct: Number(r.nulls_pct.toFixed(2)),
      });
      return {
        high: feederQuality.high.map(toRow),
        low: feederQuality.low.map(toRow),
      };
    }
    const toRow = (r: JvvnlFeederMetricResult) => ({
      entity: r.name || `#${r.entity_id}`,
      entityId: r.entity_id,
      availablePct: Number(
        (
          100 -
          (r.quality?.zero_data_pct ?? 0) -
          (r.quality?.kwh_modified_pct ?? 0)
        ).toFixed(2),
      ),
      zerosPct: Number((r.quality?.zero_data_pct ?? 0).toFixed(2)),
      nansPct: Number((r.quality?.kwh_modified_pct ?? 0).toFixed(2)),
    });
    return {
      high: feederQualityHigh.map(toRow),
      low: feederQualityLow.map(toRow),
    };
  }, [feederQualityHigh, feederQualityLow, feederQuality]);

  // Apply deep-links that arrive AFTER mount (e.g. clicking a Metric result in
  // global search while already on this page). The useState initializers only
  // read the URL once on mount, so a later ?granularity=&feeder=… change must be
  // re-applied here. Guarded by a ref so it doesn't fight the sync-to-URL effect:
  // we only act when the incoming search differs from what we last wrote out.
  const lastSyncedSearchRef = React.useRef<string | null>(null);
  const didMountRef = React.useRef(false);
  // Declared here (used by the deep-link effect below + the reset effect).
  const isFirstGranularityRender = React.useRef(true);
  useEffect(() => {
    const search = searchParams.toString();
    if (!didMountRef.current) {
      didMountRef.current = true;
      lastSyncedSearchRef.current = search;
      return; // initial mount already handled by useState initializers
    }
    if (search === lastSyncedSearchRef.current) return; // our own write, ignore

    const g = searchParams.get("granularity");
    const granularityChanged = !!g && g !== loadGranularity;
    if (g) {
      setLoadGranularity(g);
      setPrevGranularity(g); // prevent the slide-reset wiping selections
    }
    const c = searchParams.get("circle");
    setCircle((prev) =>
      c
        ? { value: Number(c), label: searchParams.get("circle_label") ?? c }
        : (prev ?? null),
    );
    const t = searchParams.get("transco");
    setTransco(
      t
        ? { value: Number(t), label: searchParams.get("transco_label") ?? t }
        : null,
    );
    const d = searchParams.get("discom");
    setDiscom(
      d
        ? { value: Number(d), label: searchParams.get("discom_label") ?? d }
        : null,
    );
    const f = searchParams.get("feeder");
    setFeeder(
      f
        ? { value: Number(f), label: searchParams.get("feeder_label") ?? f }
        : null,
    );
    const tab = searchParams.get("tab");
    if (tab === "forecast") setMetricTab("Forecast");
    else if (tab === "actual") setMetricTab("Actual");
    const rank = searchParams.get("rank");
    if (rank === "top" || rank === "bottom") setLoadRankView(rank);
    const genrank = searchParams.get("genrank");
    if (genrank === "top" || genrank === "bottom") setGenRankView(genrank);
    // Re-apply `hl` only when it actually changed (a fresh deep-link). Otherwise
    // an ordinary filter/tab change re-fires the pulse — and because
    // highlightDoneRef already matches, the clear timer never reschedules and the
    // highlight sticks on permanently across tab/page changes.
    const hl = searchParams.get("hl");
    if (hl && hl !== lastHlRef.current) {
      highlightDoneRef.current = null; // allow animate + auto-clear to run again
      setHighlightTable(hl);
    }
    lastHlRef.current = hl;
    lastSyncedSearchRef.current = search;
    // Only when granularity changes does the reset effect fire and null our
    // freshly-set selections — block it once in that case.
    if (granularityChanged) isFirstGranularityRender.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset entity selections when changing granularity (skip on initial mount)
  useEffect(() => {
    if (isFirstGranularityRender.current) {
      isFirstGranularityRender.current = false;
      return;
    }
    setTransco(null);
    setDiscom(null);
    setFeeder(null);
  }, [loadGranularity]);

  // Sync filters → URL search params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("granularity", loadGranularity);
        if (circle) {
          next.set("circle", String(circle.value));
          next.set("circle_label", String(circle.label));
        } else {
          next.delete("circle");
          next.delete("circle_label");
        }
        if (transco) {
          next.set("transco", String(transco.value));
          next.set("transco_label", String(transco.label));
        } else {
          next.delete("transco");
          next.delete("transco_label");
        }
        if (discom) {
          next.set("discom", String(discom.value));
          next.set("discom_label", String(discom.label));
        } else {
          next.delete("discom");
          next.delete("discom_label");
        }
        if (feeder) {
          next.set("feeder", String(feeder.value));
          next.set("feeder_label", String(feeder.label));
        } else {
          next.delete("feeder");
          next.delete("feeder_label");
        }
        // Record what we wrote so the deep-link-from-URL effect treats this as
        // a self-generated change and doesn't re-apply / loop.
        lastSyncedSearchRef.current = next.toString();
        return next;
      },
      { replace: true },
    );
  }, [loadGranularity, circle, transco, discom, feeder, setSearchParams]);

  return (
    <>
      <style>{`
        .jvvnl-hl-wrap {
          position: relative;
          border-radius: 20px;
          transition: transform 0.45s cubic-bezier(0.22,1,0.36,1),
                      box-shadow 0.45s ease;
        }
        /* Active table: a ring that snaps in, breathes, then releases, with a
           gentle lift. A sweeping sheen passes once across the surface. */
        .jvvnl-hl-on {
          z-index: 2;
          animation: jvvnlHlRing 2.8s cubic-bezier(0.22,1,0.36,1) 1 forwards;
        }
        .jvvnl-hl-on::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background: linear-gradient(
            115deg,
            rgba(255,255,255,0) 30%,
            rgba(255,168,107,0.35) 48%,
            rgba(255,255,255,0) 66%
          );
          background-size: 220% 100%;
          background-position: 140% 0;
          mix-blend-mode: screen;
          opacity: 0;
          animation: jvvnlHlSheen 1.5s ease-out 0.18s 1 forwards;
        }
        @keyframes jvvnlHlRing {
          0%   { transform: translateY(0) scale(1);
                 box-shadow: 0 0 0 0 rgba(226,90,31,0); }
          10%  { transform: translateY(-3px) scale(1.012);
                 box-shadow: 0 0 0 3px rgba(226,90,31,0.9),
                             0 0 0 7px rgba(226,90,31,0.18),
                             0 14px 34px rgba(226,90,31,0.28); }
          34%  { transform: translateY(-1px) scale(1.004);
                 box-shadow: 0 0 0 2px rgba(226,90,31,0.45),
                             0 0 0 6px rgba(226,90,31,0.10),
                             0 8px 22px rgba(226,90,31,0.16); }
          54%  { box-shadow: 0 0 0 3px rgba(226,90,31,0.7),
                             0 0 0 7px rgba(226,90,31,0.16),
                             0 10px 26px rgba(226,90,31,0.20); }
          100% { transform: translateY(0) scale(1);
                 box-shadow: 0 0 0 0 rgba(226,90,31,0); }
        }
        @keyframes jvvnlHlSheen {
          0%   { opacity: 0;   background-position: 140% 0; }
          15%  { opacity: 1; }
          100% { opacity: 0;   background-position: -40% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .jvvnl-hl-on { animation: jvvnlHlRingReduced 2.2s ease-out 1 forwards; }
          .jvvnl-hl-on::before { display: none; }
          @keyframes jvvnlHlRingReduced {
            0%, 70%  { box-shadow: 0 0 0 2px rgba(226,90,31,0.6); }
            100%     { box-shadow: 0 0 0 0 rgba(226,90,31,0); }
          }
        }
      `}</style>
      <div className="gas-page-container">
        <MainPageContainer className="!w-full">
          <div className="h-[calc(100vh-100px)] w-100 bg-[#FAFBFD] rounded-2xl p-4 overflow-y-auto">
            <Header
              loadGranularity={loadGranularity}
              onGranularityChange={setLoadGranularity}
              selectedLabel={
                loadGranularity === "Circle"
                  ? (circle?.label ?? null)
                  : loadGranularity === "Transco"
                    ? (transco?.label ?? null)
                    : loadGranularity === "Discom Substation"
                      ? (discom?.label ?? null)
                      : loadGranularity === "Feeder"
                        ? (feeder?.label ?? null)
                        : null
              }
              onTitleClick={(() => {
                if (loadGranularity === "Circle" && circle) {
                  return () =>
                    setModalEntity({
                      level: "circle",
                      id: String(circle.value),
                      label: String(circle.label),
                    });
                }
                if (loadGranularity === "Transco" && transco) {
                  return () =>
                    setModalEntity({
                      level: "transco",
                      id: Number(transco.value),
                      label: String(transco.label),
                    });
                }
                if (loadGranularity === "Discom Substation" && discom) {
                  return () =>
                    setModalEntity({
                      level: "discom_sub",
                      id: Number(discom.value),
                      label: String(discom.label),
                    });
                }
                if (loadGranularity === "Feeder" && feeder) {
                  return () =>
                    setModalEntity({
                      level: "feeder",
                      id: Number(feeder.value),
                      label: String(feeder.label),
                    });
                }
                return undefined;
              })()}
            />

            <div
              key={loadGranularity}
              className={`jvvnl-content-container jvvnl-animate-slide-${slideDirection}`}
            >
              <div className="flex items-end justify-between border-b border-[#DFEAF2] mb-4">
                <div className="flex items-end gap-4">
                  {METRIC_TABS.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setMetricTab(tab)}
                      title={
                        tab === "Forecast"
                          ? `Forecast data for ${forecastDate ? forecastDate.split("-").reverse().join("/") : "upcoming date"}`
                          : "Actual measured data for the selected date range"
                      }
                      className={`pb-2 text-[11px] font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                        metricTab === tab
                          ? "border-[#14171F] text-[#14171F]"
                          : "border-transparent text-[#6B7280]"
                      }`}
                    >
                      {tab === "Forecast" && forecastDate ? (
                        <>
                          FORECAST{" "}
                          <span style={{ color: "#3d6b74" }}>
                            ({forecastDate.split("-").reverse().join("/")})
                          </span>
                        </>
                      ) : (
                        tab.toUpperCase()
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 pb-1">
                  <SearchBox
                    sortAlphabetically={true}
                    options={circleList}
                    selectedOptions={circle ? [circle] : []}
                    totalCount={circleList.length}
                    onChange={(o) => {
                      setCircle(o);
                      setTransco(null);
                      setDiscom(null);
                      setFeeder(null);
                    }}
                    placeholder="Search circle..."
                    label="Circle"
                    minWidth={160}
                    isLoading={loadingEntities}
                    isClearable
                  />
                  {GRANULARITY_LEVELS[loadGranularity] >= 2 && (
                    <span className="text-gray-400 text-base font-medium select-none px-0.5">
                      ›
                    </span>
                  )}
                  {GRANULARITY_LEVELS[loadGranularity] >= 2 && (
                    <SearchBox
                      sortAlphabetically={true}
                      options={transcoList}
                      selectedOptions={transco ? [transco] : []}
                      totalCount={transcoList.length}
                      onChange={(o) => {
                        setTransco(o);
                        setDiscom(null);
                        setFeeder(null);
                      }}
                      placeholder="Search transco substation..."
                      label="Transco Substation"
                      width={200}
                      minWidth={190}
                      isLoading={loadingEntities}
                      isClearable
                    />
                  )}
                  {GRANULARITY_LEVELS[loadGranularity] >= 3 && (
                    <span className="text-gray-400 text-base font-medium select-none px-0.5">
                      ›
                    </span>
                  )}
                  {GRANULARITY_LEVELS[loadGranularity] >= 3 && (
                    <SearchBox
                      sortAlphabetically={true}
                      options={discomList}
                      selectedOptions={discom ? [discom] : []}
                      totalCount={discomList.length}
                      onChange={(o) => {
                        setDiscom(o);
                        setFeeder(null);
                        if (o) {
                          const raw = allDiscomSubs.find(
                            (d) => d.discom_substation_id === o.value,
                          );
                          if (raw?.transco_substation_id) {
                            const t = transcoList.find(
                              (x) => x.value === raw.transco_substation_id,
                            );
                            if (t) setTransco(t);
                          }
                        }
                      }}
                      placeholder="Search discom substation..."
                      label="Discom Substation"
                      width={200}
                      minWidth={190}
                      isLoading={loadingEntities}
                      isClearable
                    />
                  )}
                  {GRANULARITY_LEVELS[loadGranularity] >= 4 && (
                    <span className="text-gray-400 text-base font-medium select-none px-0.5">
                      ›
                    </span>
                  )}
                  {GRANULARITY_LEVELS[loadGranularity] >= 4 && (
                    <SearchBox
                      sortAlphabetically={true}
                      options={feederList}
                      totalCount={feederList.length}
                      selectedOptions={feeder ? [feeder] : []}
                      onChange={(o) => {
                        setFeeder(o);
                        if (o) {
                          const rawF = allFeeders.find(
                            (f) => f.feeder_id === o.value,
                          );
                          if (rawF?.discom_substation_id) {
                            const rawD = allDiscomSubs.find(
                              (d) =>
                                d.discom_substation_id ===
                                rawF.discom_substation_id,
                            );
                            if (rawD) {
                              setDiscom({
                                value: rawD.discom_substation_id,
                                label: rawD.discom_substation_name,
                              });
                              if (rawD.transco_substation_id) {
                                const t = transcoList.find(
                                  (x) => x.value === rawD.transco_substation_id,
                                );
                                if (t) setTransco(t);
                              }
                            }
                          }
                        }
                      }}
                      placeholder="Search feeder..."
                      label="Feeder"
                      minWidth={160}
                      isLoading={loadingEntities}
                      isClearable
                    />
                  )}
                </div>
              </div>

              {(() => {
                const isFeederForecast =
                  loadGranularity === "Feeder" && metricTab === "Forecast";
                const isFeederActual =
                  loadGranularity === "Feeder" && metricTab === "Actual";
                const displayCards = isFeederForecast
                  ? metricCards.filter(
                      (c) => c.title === "Energy" || c.title === "Load",
                    )
                  : isFeederActual
                    ? metricCards.filter(
                        (c) =>
                          c.title !== "Generation" && c.title !== "Net Load",
                      )
                    : metricCards;
                const dataSummaryCard = isFeederActual
                  ? (() => {
                      // Aggregate quality over the scope's feeders (combined
                      // feeder-metrics), independent of the card data source.
                      const full = dataSummary?.metrics?.full;
                      return (
                        <DataSummaryCard
                          availableValue={full?.available_pct ?? null}
                          zerosValue={full?.zeros_pct ?? null}
                          nullsValue={full?.nulls_pct ?? null}
                        />
                      );
                    })()
                  : null;
                return (
                  <div
                    className={`grid gap-1 mb-4 ${isFeederActual ? "grid-cols-3" : isFeederForecast ? "grid-cols-2" : "grid-cols-4"}`}
                  >
                    {loadingCombined
                      ? Array.from({ length: displayCards.length || 4 }).map(
                          (_, i) => <MetricCardSkeleton key={i} />,
                        )
                      : displayCards.map((card) => {
                          if (metricTab !== "Forecast") {
                            return (
                              <div
                                key={card.title}
                                className={hlClass(CARD_HL[card.title])}
                              >
                                <MetricCard
                                  {...card}
                                  autoScaleUnit={autoScaleUnit}
                                />
                              </div>
                            );
                          }
                          const fFull = forecastDemand?.metrics.full;
                          const toMw = (
                            mw: string | number | null | undefined,
                          ): number | null => (mw == null ? null : roundMw(mw));
                          const toMwh = (
                            mwh: string | number | null | undefined,
                          ): number | null =>
                            mwh == null ? null : roundMwh(mwh);
                          let fPeak: number | null = null;
                          let fAvg: number | null = null;
                          let pPeak: number | undefined = undefined;
                          let pAvg: number | undefined = undefined;
                          let fPeakWeather = null as ReturnType<
                            typeof toWeatherStats
                          >;
                          let fAvgWeather = null as ReturnType<
                            typeof toWeatherStats
                          >;
                          let fPeakDate: string | undefined;
                          let fPeakSlot: number | null | undefined;

                          if (card.title === "Energy") {
                            fPeak = toMwh(fFull?.total_mwh);
                            fAvg = toMwh(fFull?.avg_mwh);
                            pPeak =
                              toMwh(fFull?.previous_total_mwh) ?? undefined;
                            pAvg =
                              fFull?.previous_avg_mwh != null
                                ? (toMwh(fFull.previous_avg_mwh) ?? undefined)
                                : undefined;
                            fPeakWeather = toWeatherStats(
                              fFull?.previous_avg_weather,
                            );
                            fAvgWeather = toWeatherStats(fFull?.avg_weather);
                            fPeakDate = undefined;
                            fPeakSlot = undefined;
                          } else if (card.title === "Load") {
                            fPeak = toMw(fFull?.peak_mw);
                            fAvg = toMw(fFull?.avg_mw);
                            pPeak = toMw(fFull?.value_at_peak_mw) ?? undefined;
                            pAvg = toMw(fFull?.previous_avg_mw) ?? undefined;
                            fPeakWeather = toWeatherStats(
                              fFull?.previous_day_peak_weather,
                            );
                            fAvgWeather = toWeatherStats(fFull?.peak_weather);
                            fPeakDate = fFull?.peak_date ?? undefined;
                            fPeakSlot = fFull?.peak_slot_48 ?? null;
                          } else if (card.title === "Generation") {
                            fPeak = toMw(fFull?.peak_generation_mw);
                            fAvg = toMw(fFull?.avg_generation_mw);
                            pPeak =
                              toMw(fFull?.value_at_peak_generation_mw) ??
                              undefined;
                            pAvg =
                              toMw(fFull?.previous_avg_generation_mw) ??
                              undefined;
                            fPeakWeather = toWeatherStats(
                              fFull?.previous_day_peak_generation_weather,
                            );
                            fAvgWeather = toWeatherStats(
                              fFull?.peak_generation_weather,
                            );
                            fPeakDate =
                              fFull?.peak_generation_date ?? undefined;
                            fPeakSlot = fFull?.peak_generation_slot_48 ?? null;
                          } else if (card.title === "Net Load") {
                            fPeak = toMw(fFull?.peak_net_mw);
                            fAvg = toMw(fFull?.avg_net_mw);
                            pPeak =
                              toMw(fFull?.value_at_peak_net_mw) ?? undefined;
                            pAvg =
                              toMw(fFull?.previous_avg_net_mw) ?? undefined;
                            fPeakWeather = toWeatherStats(
                              fFull?.peak_net_weather,
                            );
                            fAvgWeather = toWeatherStats(
                              fFull?.previous_day_peak_net_weather,
                            );
                            fPeakDate = fFull?.peak_net_date ?? undefined;
                            fPeakSlot = fFull?.peak_net_slot_48 ?? null;
                          }
                          return (
                            <div
                              key={card.title}
                              className={hlClass(CARD_HL[card.title])}
                            >
                              <MetricCard
                                {...card}
                                peakValue={fPeak}
                                averageValue={fAvg}
                                peakPrevDay={pPeak}
                                averagePrevDay={pAvg}
                                peakWeather={fPeakWeather}
                                avgWeather={fAvgWeather}
                                peakDate={fPeakDate}
                                peakSlot={fPeakSlot}
                                showPeakWeather
                                autoScaleUnit={autoScaleUnit}
                              />
                            </div>
                          );
                        })}
                    {dataSummaryCard && (
                      <div className={hlClass("card-datasummary")}>
                        {dataSummaryCard}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="grid grid-cols-3 gap-1 mb-4">
                {loadingCombined
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <PeakCardSkeleton key={i} />
                    ))
                  : peakCards.map((card) => {
                      const bucketKey =
                        card.title === "Morning Peak"
                          ? "morning"
                          : card.title === "Solar Peak"
                            ? "solar"
                            : "evening";
                      const fBucket =
                        metricTab === "Forecast"
                          ? forecastDemand?.metrics?.[
                              bucketKey as "morning" | "solar" | "evening"
                            ]
                          : null;
                      // Forecast: override bucket peak with forecast value + show
                      // prev-day comparison from value_at_peak_mw (same slot, day-before).
                      const fPeakValue =
                        fBucket?.peak_mw != null
                          ? roundMw(fBucket.peak_mw)
                          : null;
                      const fPrevPeak =
                        fBucket?.value_at_peak_mw != null
                          ? roundMw(fBucket.value_at_peak_mw)
                          : null;
                      return (
                        <div
                          key={card.title}
                          className={hlClass(CARD_HL[card.title])}
                        >
                          <PeakCard
                            {...card}
                            peakValue={
                              metricTab === "Forecast"
                                ? fPeakValue
                                : card.peakValue
                            }
                            peakBlock={
                              metricTab === "Forecast"
                                ? (fBucket?.peak_slot_48 ?? null)
                                : card.peakBlock
                            }
                            peakDate={
                              metricTab === "Forecast"
                                ? (fBucket?.peak_date ?? null)
                                : card.peakDate
                            }
                            weather={
                              metricTab === "Forecast"
                                ? toWeatherStats(fBucket?.peak_weather)
                                : card.weather
                            }
                            peakPrevDay={
                              metricTab === "Forecast" ? fPrevPeak : null
                            }
                            weatherPrevDay={
                              metricTab === "Forecast"
                                ? toWeatherStats(
                                    fBucket?.previous_day_peak_weather,
                                  )
                                : null
                            }
                            showPrevDay={metricTab === "Forecast"}
                            autoScaleUnit={autoScaleUnit}
                          />
                        </div>
                      );
                    })}
              </div>

              {loadGranularity !== "Feeder" && (
                <div className="grid grid-cols-2 gap-4">
                  {(
                    metricTab === "Forecast"
                      ? loadingForecastRank || !forecastRankReady
                      : loadingRank || !rankReady
                  ) ? (
                    <>
                      <RankTableSkeleton />
                      <RankTableSkeleton />
                      <RankTableSkeleton />
                      <RankTableSkeleton />
                    </>
                  ) : (
                    <>
                      {/* Load */}
                      <div
                        className={`jvvnl-hl-wrap ${highlightTable === "load-top" || highlightTable === "load-bottom" ? "jvvnl-hl-on" : ""}`}
                      >
                        <RankTable
                          title={
                            loadGranularity === "Circle"
                              ? "Transco Substation"
                              : loadGranularity === "Transco"
                                ? "Discom Substation"
                                : loadGranularity === "Discom Substation"
                                  ? "Feeder"
                                  : "Substation"
                          }
                          entityLabel={
                            loadGranularity === "Circle"
                              ? "Transco Substation"
                              : loadGranularity === "Transco"
                                ? "Discom Substation"
                                : loadGranularity === "Discom Substation"
                                  ? "Feeder"
                                  : "Substation"
                          }
                          dotColor="#1F7A4D"
                          tag="Load Contribution"
                          rows={
                            loadRankView === "top"
                              ? rankRows.loadTop
                              : rankRows.loadBottom
                          }
                          primaryWeather={primaryWeather}
                          onWeatherChange={setPrimaryWeather}
                          loadUnit={loadUnit}
                          energyUnit={energyUnit}
                          rankView={loadRankView}
                          onRankViewChange={setLoadRankView}
                          autoScaleUnit={autoScaleUnit}
                          onHeadingClick={() => setListModal("load")}
                          onRowClick={
                            loadGranularity === "Discom Substation"
                              ? (row) => {
                                  if (row.entityId == null) return;
                                  setModalEntity({
                                    level: "feeder",
                                    id: row.entityId,
                                    label: row.entity,
                                  });
                                }
                              : loadGranularity === "Circle" ||
                                  loadGranularity === "Transco"
                                ? (row) => {
                                    if (row.entityId == null) return;
                                    setModalEntity({
                                      level:
                                        loadGranularity === "Circle"
                                          ? "transco"
                                          : "discom_sub",
                                      id: row.entityId,
                                      label: row.entity,
                                    });
                                  }
                                : undefined
                          }
                        />
                      </div>
                      {/* Generation */}
                      <div
                        className={`jvvnl-hl-wrap ${highlightTable === "gen" ? "jvvnl-hl-on" : ""}`}
                      >
                        <RankTable
                          title="DRE Generators"
                          entityLabel="DRE Generators"
                          dotColor="#606679"
                          tag="Generation Contribution"
                          rows={
                            genRankView === "top"
                              ? rankRows.genTop
                              : rankRows.genBottom
                          }
                          primaryWeather={primaryWeather}
                          onWeatherChange={setPrimaryWeather}
                          loadUnit={loadUnit}
                          energyUnit={energyUnit}
                          rankView={genRankView}
                          onRankViewChange={setGenRankView}
                          autoScaleUnit={autoScaleUnit}
                          onHeadingClick={() => setListModal("gen")}
                          onRowClick={(row) => {
                            if (row.entityId == null) return;
                            setModalEntity({
                              level: "plant",
                              id: row.entityId,
                              label: row.entity,
                            });
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {loadGranularity === "Feeder" && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[13px] font-medium text-[#14171F]">
                      Feeder Summary
                    </span>
                    <FilterTab
                      options={FEEDER_VIEW_TABS}
                      selected={[feederView]}
                      onClick={(opt) => setFeederView(opt)}
                      variant="pill-outline"
                    />
                  </div>

                  {feederView === "Table" &&
                    (loadingFeederQuality ? (
                      <div className="grid grid-cols-2 gap-4">
                        <RankTableSkeleton />
                        <RankTableSkeleton />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div
                          className={`jvvnl-hl-wrap ${highlightTable === "fq-high" ? "jvvnl-hl-on" : ""}`}
                        >
                          <FeederTable
                            title="List of Feeder ≥ 80% Data Availability"
                            dotColor="#1F7A4D"
                            tag="Load Contribution"
                            rows={feederQualityRows.high}
                            onRowClick={(row) => {
                              if (row.entityId == null) return;
                              setModalEntity({
                                level: "feeder",
                                id: row.entityId,
                                label: row.entity,
                              });
                            }}
                          />
                        </div>
                        <div
                          className={`jvvnl-hl-wrap ${highlightTable === "fq-low" ? "jvvnl-hl-on" : ""}`}
                        >
                          <FeederTable
                            title="List of Feeder < 20% Data Availability"
                            dotColor="#E25A1F"
                            tag="Energy"
                            rows={feederQualityRows.low}
                            onRowClick={(row) => {
                              if (row.entityId == null) return;
                              setModalEntity({
                                level: "feeder",
                                id: row.entityId,
                                label: row.entity,
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}

                  {feederView === "Chart" && (
                    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3">
                      <ReMixChart
                        showCriticalPeriods={true}
                        activePeriodKinds={criticalPeriodKinds}
                        onActivePeriodKindsChange={setCriticalPeriodKinds}
                        brushIndex={{
                          startIndex: 0,
                          endIndex: Math.max(
                            0,
                            Math.ceil(
                              (feederChartData?.data?.length ?? 1) / 3,
                            ) - 1,
                          ),
                        }}
                        data={feederChartData.data}
                        legends={feederChartData.legends}
                        xDataKey="name"
                        unit="kWh"
                        yAxisLabel="kWh"
                        isLoading={loadingFeederChart}
                        chartContainerHeight="230px"
                        onlyTitle
                        onBarClick={(params) => {
                          const row =
                            feederChartRows.find(
                              (r) => r.name === params.name,
                            ) ?? feederChartRows[params.dataIndex ?? -1];
                          if (!row) return;
                          const realName =
                            allFeeders.find(
                              (f) => f.feeder_id === row.entity_id,
                            )?.feeder_name ?? row.name;
                          setModalEntity({
                            level: "feeder",
                            id: row.entity_id,
                            label: realName,
                          });
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5">
              <Tooltip
                note={<p>Source:</p>}
                text="1. Weather : Open-Meteo&#10;2. Load : Discom&#10;3. Feeder : FDRMS/FOXMS&#10;4. Generation : HKRP Innovations&#10;"
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
      {modalEntity && (
        <div
          className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setModalEntity(null)}
        >
          <div
            className="bg-gray-100 rounded-2xl w-[95vw] h-[96vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <NetConsumptionPopup
              embedded={modalEntity}
              onClose={() => setModalEntity(null)}
              initialTab={metricTab}
              // Forecast tab defaults the popup date to the model's forecast_date
              // (single day). User can still change it via the picker.
              forecastDateOverride={
                metricTab === "Forecast" ? forecastDate : null
              }
            />
          </div>
        </div>
      )}
      {listModal &&
        (() => {
          const childLabel =
            loadGranularity === "Circle"
              ? "Transco Substation"
              : loadGranularity === "Transco"
                ? "Discom Substation"
                : loadGranularity === "Discom Substation"
                  ? "Feeder"
                  : "Substation";
          const isGen = listModal === "gen";
          const childModalLevel: "transco" | "discom_sub" | "feeder" =
            loadGranularity === "Circle"
              ? "transco"
              : loadGranularity === "Transco"
                ? "discom_sub"
                : "feeder";
          return (
            <RankListModal
              title={isGen ? "DRE Generators" : childLabel}
              entityLabel={isGen ? "DRE Generators" : childLabel}
              tag={isGen ? "Generation Contribution" : "Load Contribution"}
              dotColor={isGen ? "#606679" : "#1F7A4D"}
              rows={isGen ? listModalData.gen : listModalData.load}
              loading={listModalLoading}
              primaryWeather={primaryWeather}
              onWeatherChange={setPrimaryWeather}
              loadUnit={loadUnit}
              energyUnit={energyUnit}
              onClose={() => setListModal(null)}
              onRowClick={(row) => {
                if (row.entityId == null) return;
                setListModal(null);
                setModalEntity({
                  level: isGen ? "plant" : childModalLevel,
                  id: row.entityId,
                  label: row.entity,
                });
              }}
            />
          );
        })()}
    </>
  );
};

export default JvvnlMetric;
